/**
 * Cycles product card images on collection/featured grids (data-card-image-autoplay on .media--card-autoplay).
 */
(function () {
  class CardImageAutoplay {
    constructor(mediaRoot) {
      this.active = false;
      this.mediaRoot = mediaRoot;
      this.imgs = Array.from(mediaRoot.querySelectorAll('.card-autoplay-img'));
      if (this.imgs.length < 2) return;

      this.wrapper = mediaRoot.closest('.card-wrapper--autoplay-images');
      if (!this.wrapper) return;

      this._timer = null;
      this._hoverPaused = false;
      this._docHidden = false;
      this.idx = 0;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (this.reducedMotion.matches) return;

      const s = parseFloat(mediaRoot.dataset.cardAutoplaySpeed);
      this.speedMs = Math.max(2000, (Number.isFinite(s) ? s : 4) * 1000);

      this._tick = this.tick.bind(this);
      this._onEnter = this.pauseHover.bind(this);
      this._onLeave = this.resumeHover.bind(this);
      this._onFocusIn = this.pauseHover.bind(this);
      this._onFocusOut = this.onFocusOut.bind(this);
      this._onVisibility = this.onVisibility.bind(this);
      this._onMotionChange = this.onMotionChange.bind(this);

      this.bind();
      this.start();
      this.active = true;
    }

    tick() {
      if (!this.canRun()) return;
      this.imgs[this.idx].classList.remove('is-active');
      this.idx = (this.idx + 1) % this.imgs.length;
      this.imgs[this.idx].classList.add('is-active');
    }

    start() {
      this.stop();
      if (!this.canRun()) return;
      this._timer = window.setInterval(this._tick, this.speedMs);
    }

    stop() {
      if (this._timer) {
        clearInterval(this._timer);
        this._timer = null;
      }
    }

    canRun() {
      if (this.reducedMotion.matches) return false;
      if (this._hoverPaused || this._docHidden) return false;
      return true;
    }

    syncRunState() {
      if (this.canRun() && !this._timer) this.start();
      if (!this.canRun()) this.stop();
    }

    pauseHover() {
      this._hoverPaused = true;
      this.syncRunState();
    }

    resumeHover() {
      this._hoverPaused = false;
      this.syncRunState();
    }

    onFocusOut() {
      window.setTimeout(() => {
        if (!this.wrapper.contains(document.activeElement)) {
          this._hoverPaused = false;
          this.syncRunState();
        }
      }, 0);
    }

    onVisibility() {
      this._docHidden = document.hidden;
      this.syncRunState();
    }

    onMotionChange() {
      this.syncRunState();
    }

    bind() {
      this.wrapper.addEventListener('mouseenter', this._onEnter);
      this.wrapper.addEventListener('mouseleave', this._onLeave);
      this.wrapper.addEventListener('focusin', this._onFocusIn);
      this.wrapper.addEventListener('focusout', this._onFocusOut);
      document.addEventListener('visibilitychange', this._onVisibility);
      this.reducedMotion.addEventListener('change', this._onMotionChange);
    }

    destroy() {
      this.stop();
      this.wrapper.removeEventListener('mouseenter', this._onEnter);
      this.wrapper.removeEventListener('mouseleave', this._onLeave);
      this.wrapper.removeEventListener('focusin', this._onFocusIn);
      this.wrapper.removeEventListener('focusout', this._onFocusOut);
      document.removeEventListener('visibilitychange', this._onVisibility);
      this.reducedMotion.removeEventListener('change', this._onMotionChange);
      this.mediaRoot.removeAttribute('data-card-autoplay-initialized');
    }
  }

  function initFromRoot(root) {
    root.querySelectorAll('.media--card-autoplay[data-card-image-autoplay]').forEach((el) => {
      if (el.hasAttribute('data-card-autoplay-initialized')) return;
      const ctrl = new CardImageAutoplay(el);
      if (!ctrl.active) return;
      el.setAttribute('data-card-autoplay-initialized', '');
      el._cardImageAutoplay = ctrl;
    });
  }

  function destroyInRoot(root) {
    root.querySelectorAll('.media--card-autoplay[data-card-autoplay-initialized]').forEach((el) => {
      if (el._cardImageAutoplay) {
        el._cardImageAutoplay.destroy();
        delete el._cardImageAutoplay;
      }
    });
  }

  function boot() {
    initFromRoot(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target) initFromRoot(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target) destroyInRoot(event.target);
  });

  window.SpecrInitCardImageAutoplay = initFromRoot;
  window.SpecrDestroyCardImageAutoplay = destroyInRoot;
})();
